import React from "react";

export const Skeleton = ({
  width,
  height,
  circle = false,
  className = "",
  style = {},
}) => (
  <span
    className={`skeleton ${circle ? "skeleton-circle" : ""} ${className}`.trim()}
    style={{
      width: width ?? "100%",
      height: height ?? 14,
      ...style,
    }}
    aria-hidden="true"
  />
);

export const ShopCardSkeleton = () => (
  <div className="shop-card skeleton-card">
    <Skeleton className="skeleton-shop-avatar" circle />
    <div className="skeleton-shop-body">
      <Skeleton height={19} width="55%" />
      <Skeleton height={14} width="40%" style={{ marginTop: 8 }} />
      <Skeleton height={10} width="75%" style={{ marginTop: 8 }} />
    </div>
  </div>
);

export const ShopListSkeleton = ({ count = 5 }) => (
  <div className="shop-main skeleton-list">
    {Array.from({ length: count }, (_, index) => (
      <ShopCardSkeleton key={index} />
    ))}
  </div>
);

export const OrderCardSkeleton = () => (
  <div className="orders-card skeleton-card">
    <Skeleton className="skeleton-order-avatar" circle />
    <div className="skeleton-order-body">
      <Skeleton height={15} width="50%" />
      <Skeleton height={15} width="65%" style={{ marginTop: 8 }} />
      <Skeleton height={14} width="35%" style={{ marginTop: 10 }} />
      <Skeleton height={28} width="45%" style={{ marginTop: 12, borderRadius: 14 }} />
    </div>
  </div>
);

export const OrderListSkeleton = ({ count = 5, title = true }) => (
  <div className="orders-main skeleton-list">
    {title && <Skeleton className="skeleton-page-title" height={32} width="45%" />}
    {Array.from({ length: count }, (_, index) => (
      <OrderCardSkeleton key={index} />
    ))}
  </div>
);

export const ProductCardSkeleton = () => (
  <div className="product-card skeleton-card">
    <div className="row-1">
      <Skeleton className="skeleton-product-img" />
      <div className="skeleton-product-body">
        <Skeleton height={18} width="72%" />
        <Skeleton height={14} width="48%" style={{ marginTop: 10 }} />
        <Skeleton height={14} width="38%" style={{ marginTop: 8 }} />
      </div>
    </div>
    <div className="row-2 skeleton-product-row-2">
      <Skeleton height={32} width="38%" style={{ borderRadius: 8 }} />
      <Skeleton height={36} width={36} circle />
    </div>
  </div>
);

export const ProductListSkeleton = ({ count = 4 }) => (
  <div className="product-main skeleton-list">
    {Array.from({ length: count }, (_, index) => (
      <ProductCardSkeleton key={index} />
    ))}
  </div>
);

export const LoadMoreSkeleton = ({ variant = "order" }) => (
  <div className="skeleton-load-more">
    {variant === "shop" && <ShopCardSkeleton />}
    {variant === "order" && <OrderCardSkeleton />}
    {variant === "product" && <ProductCardSkeleton />}
  </div>
);

export const ProfileSkeleton = () => (
  <div className="profile-main skeleton-list">
    <div className="profile-img-div skeleton-profile-head">
      <Skeleton className="skeleton-profile-avatar" circle />
    </div>
    <Skeleton height={24} width="50%" style={{ margin: "16px auto" }} />
    <Skeleton height={18} width="60%" style={{ margin: "0 auto 24px" }} />
    <div className="px-20">
      <div className="profile-data skeleton-profile-row">
        <Skeleton height={48} width="28%" />
        <Skeleton height={48} width="28%" />
        <Skeleton height={48} width="28%" />
      </div>
      <div className="profile-data skeleton-profile-row">
        <Skeleton height={48} width="45%" />
        <Skeleton height={48} width="45%" />
      </div>
      <Skeleton height={49} width="100%" style={{ marginTop: 24, borderRadius: 26 }} />
      <Skeleton height={49} width="100%" style={{ marginTop: 12, borderRadius: 26 }} />
    </div>
  </div>
);

export const NotificationListSkeleton = ({ count = 4 }) => (
  <div className="skeleton-list">
    {Array.from({ length: count }, (_, index) => (
      <div key={index} className="orders-card skeleton-card">
        <div className="skeleton-order-body" style={{ width: "100%" }}>
          <Skeleton height={15} width="40%" />
          <Skeleton height={14} width="85%" style={{ marginTop: 10 }} />
          <Skeleton height={20} width="30%" style={{ marginTop: 14 }} />
        </div>
      </div>
    ))}
  </div>
);

export const ReportsSkeleton = () => (
  <div className="reports-status-grid skeleton-list">
    {Array.from({ length: 6 }, (_, index) => (
      <div key={index} className="reports-status-card skeleton-card">
        <Skeleton height={18} width="55%" />
        <div className="reports-status-metrics" style={{ marginTop: 16 }}>
          <Skeleton height={40} width="42%" />
          <Skeleton height={40} width="42%" />
        </div>
      </div>
    ))}
  </div>
);

export const FactorPageSkeleton = () => (
  <div className="new-fac-main skeleton-list">
    <div className="pay-fac-head skeleton-factor-head">
      <Skeleton height={24} width={24} circle />
      <Skeleton height={24} width={24} circle />
      <Skeleton height={24} width={24} circle />
    </div>
    <Skeleton height={22} width="35%" style={{ marginTop: 20 }} />
    <Skeleton height={16} width="55%" style={{ marginTop: 12 }} />
    <Skeleton height={16} width="45%" style={{ marginTop: 8 }} />
    {Array.from({ length: 3 }, (_, index) => (
      <div key={index} className="product-card skeleton-card" style={{ marginTop: 18 }}>
        <div className="row-1">
          <Skeleton className="skeleton-product-img" />
          <div className="skeleton-product-body">
            <Skeleton height={16} width="60%" />
            <Skeleton height={14} width="40%" style={{ marginTop: 8 }} />
          </div>
        </div>
      </div>
    ))}
    <Skeleton height={52} width="100%" style={{ marginTop: 24, borderRadius: 26 }} />
  </div>
);

export const PaymentPageSkeleton = () => (
  <div className="pay-main skeleton-list">
    <Skeleton height={20} width="25%" style={{ margin: "0 auto 20px" }} />
    <div className="skeleton-payment-tabs">
      <Skeleton height={36} width="30%" style={{ borderRadius: 8 }} />
      <Skeleton height={36} width="30%" style={{ borderRadius: 8 }} />
      <Skeleton height={36} width="30%" style={{ borderRadius: 8 }} />
    </div>
    {Array.from({ length: 4 }, (_, index) => (
      <Skeleton key={index} height={44} width="100%" style={{ marginTop: 14, borderRadius: 12 }} />
    ))}
    <Skeleton height={80} width="100%" style={{ marginTop: 14, borderRadius: 12 }} />
    <Skeleton height={49} width="100%" style={{ marginTop: 24, borderRadius: 26 }} />
  </div>
);

export const ShopFormSkeleton = () => (
  <div className="add-cus-main skeleton-list">
    <Skeleton height={28} width="40%" style={{ marginBottom: 20 }} />
    {Array.from({ length: 8 }, (_, index) => (
      <Skeleton key={index} height={44} width="100%" style={{ marginTop: 10, borderRadius: 12 }} />
    ))}
    <Skeleton height={120} width="100%" style={{ marginTop: 12, borderRadius: 12 }} />
    <Skeleton height={200} width="100%" style={{ marginTop: 12, borderRadius: 12 }} />
  </div>
);

export const SubmitButtonSkeleton = ({ width = 72 }) => (
  <Skeleton className="skeleton-btn-inline" height={20} width={width} />
);

export const IconSkeleton = ({ size = 22 }) => (
  <Skeleton className="factor-head-icon skeleton-icon-inline" height={size} width={size} circle />
);

export const DropdownListSkeleton = ({ count = 4 }) => (
  <div className="skeleton-dropdown-list">
    {Array.from({ length: count }, (_, index) => (
      <Skeleton key={index} height={16} width={`${70 - index * 8}%`} style={{ marginTop: index ? 10 : 0 }} />
    ))}
  </div>
);

export const CartBarSkeleton = () => (
  <div className="add-to-cart skeleton-cart-bar">
    <Skeleton height={20} width={120} />
  </div>
);

export const OcrLoadingSkeleton = () => (
  <div className="pos-receipt-ocr-loading skeleton-ocr-loading">
    <Skeleton height={32} width={32} circle />
    <span>در حال خواندن رسید...</span>
  </div>
);
